import type { ReviewWithReplyDto } from '@up2stay/up2promo-api-client';
import {
  Badge,
  Button,
  Card,
  previewPanelSurfaceClass,
  cn,
} from '@up2stay/up2promo-ui';
import { Loader2, Send, Sparkles } from 'lucide-react';

import { formatRelativeTime } from '@/core/components/leads/lead-detail/format-relative-time';
import { StarRating } from '@/core/components/reputation/reputation-bits';
import { ReviewAuthorAvatar } from '@/core/components/reputation/reviews/review-list-item';
import type { ReviewsInbox } from '@/core/components/reputation/reviews/use-reviews-inbox';
import { useEffectiveReplyStyle } from '@/core/hooks/use-effective-reply-style';

/**
 * Mobile card for an urgent (critical, unanswered) review: full text plus the
 * AI draft with quick Edit / Publish, so the worst review can be answered
 * without opening the detail sheet.
 */
export function ReviewUrgentCard({
  inbox,
  review,
  onOpen,
}: {
  inbox: ReviewsInbox;
  review: ReviewWithReplyDto;
  onOpen: () => void;
}) {
  const t = inbox.reviewsDict.urgent;
  const style = useEffectiveReplyStyle(review.locationId);
  const draft = review.replyDraft?.text?.trim() ?? '';
  const publishing = inbox.isPublishing(review.id);
  const location = inbox.locationNameById.get(review.locationId);
  const styleName =
    style.kind === 'template' && style.templateName
      ? ` · ${style.templateName}`
      : '';

  return (
    <Card className="flex flex-col gap-3 p-4" data-review-id={review.id}>
      <div className="flex items-center gap-2.5">
        <ReviewAuthorAvatar
          name={review.authorName}
          className="size-9 text-xs"
        />
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-semibold">
            {review.authorName}
          </div>
          <div className="text-muted-foreground truncate text-[11.5px]">
            {[formatRelativeTime(review.reviewedAt, inbox.locale), location]
              .filter(Boolean)
              .join(' · ')}
          </div>
        </div>
        <Badge variant="destructive">{t.badge}</Badge>
      </div>
      <StarRating rating={review.rating} />
      {review.text ? (
        <p className="text-sm leading-[1.55]">
          {'„'}
          {review.text}
          {'“'}
        </p>
      ) : null}

      <div
        className={cn(
          'flex flex-col gap-2.5 rounded-[14px] border p-3',
          previewPanelSurfaceClass,
        )}
      >
        <div className="flex items-center gap-2 text-[12.5px] font-bold text-[color:var(--ghost-accent-fg)]">
          <Sparkles className="size-3.5" aria-hidden />
          {t.draft}
          {styleName}
        </div>
        {draft ? <p className="text-[13px] leading-[1.55]">{draft}</p> : null}
        <div className="flex gap-2">
          <Button
            variant="outline"
            className="h-11 flex-1"
            onClick={onOpen}
            disabled={publishing}
          >
            {draft ? t.edit : t.reply}
          </Button>
          {draft ? (
            <Button
              className="h-11 flex-[1.4]"
              disabled={publishing}
              onClick={() => void inbox.publish(review, draft)}
            >
              {publishing ? (
                <Loader2 className="mr-1.5 size-3.5 animate-spin" aria-hidden />
              ) : (
                <Send className="mr-1.5 size-3.5" aria-hidden />
              )}
              {inbox.reviewsDict.reply.publish}
            </Button>
          ) : null}
        </div>
      </div>
    </Card>
  );
}
